/** Normalized error surface for every adapter (HTTP, mock, future providers). */

export type BackendErrorCode =
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'rate_limited'
  | 'timeout'
  | 'network'
  | 'canceled'
  | 'bad_request'
  | 'server'
  | 'unknown'

const RETRYABLE: ReadonlySet<BackendErrorCode> = new Set([
  'rate_limited',
  'timeout',
  'network',
  'server',
])

export interface BackendErrorInit {
  code: BackendErrorCode
  message?: string
  status?: number
  requestId?: string
  cause?: unknown
}

export class BackendError extends Error {
  readonly code: BackendErrorCode
  readonly status?: number
  readonly retryable: boolean
  readonly requestId?: string

  constructor(init: BackendErrorInit) {
    super(init.message ?? init.code)
    this.name = 'BackendError'
    this.code = init.code
    this.status = init.status
    this.requestId = init.requestId
    this.retryable = RETRYABLE.has(init.code)
    if (init.cause !== undefined) this.cause = init.cause
  }
}

export function codeFromStatus(status: number): BackendErrorCode {
  switch (status) {
    case 400:
      return 'bad_request'
    case 401:
      return 'unauthorized'
    case 403:
      return 'forbidden'
    case 404:
      return 'not_found'
    case 408:
      return 'timeout'
    case 429:
      return 'rate_limited'
    default:
      return status >= 500 ? 'server' : 'unknown'
  }
}

export async function backendErrorFromResponse(res: Response): Promise<BackendError> {
  let message = res.statusText
  const requestId = res.headers.get('x-request-id') ?? undefined
  try {
    const body = (await res.clone().json()) as {
      message?: string
      // OpenAI-compatible proxies (LiteLLM) nest the text: { error: { message } }.
      error?: string | { message?: string }
    }
    const nested = typeof body.error === 'string' ? body.error : body.error?.message
    message = body.message ?? nested ?? message
  } catch {
    // non-JSON error body — keep statusText
  }
  return new BackendError({
    code: codeFromStatus(res.status),
    status: res.status,
    message,
    requestId,
  })
}

export function backendErrorFromUnknown(e: unknown): BackendError {
  if (e instanceof BackendError) return e
  if (e instanceof DOMException && (e.name === 'AbortError' || e.name === 'TimeoutError')) {
    const code = e.name === 'TimeoutError' ? 'timeout' : 'canceled'
    return new BackendError({ code, message: e.message })
  }
  const message = e instanceof Error ? e.message : 'Unknown error'
  return new BackendError({ code: 'network', message, cause: e })
}
